import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  BiData,
  BiContextProvidedValue,
  RequestNowClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import BrandedButton, {
  BrandedButtonProps,
} from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import { pushAdoptsOrderOriginalDeviationModal } from '../../Modals/redux/actionCreators';
import { useRestrictToLoggedIn } from '../../utils/hooks/useRestrictToLoggedIn';
import { normalizeOriginalsCompletedOrders } from '../../utils/originalsCompletedOrders';

interface Props extends Pick<BrandedButtonProps, 'className' | 'size'> {
  deviation: PapiDeviation;
  sectionname?: string | null;
}

export const RequestOriginalButton: React.FC<Props> = ({
  deviation,
  className,
  size,
  sectionname,
}) => {
  const { t } = useTranslation();
  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const logBiEvent = useBiLogger();
  const dispatchPushAdoptsOrderOriginalDeviationModal = useAction(
    pushAdoptsOrderOriginalDeviationModal
  );

  const originals = deviation.extended?.originals ?? deviation.originals;
  const { total: completedOrdersCount } =
    normalizeOriginalsCompletedOrders(originals);

  const handleRequestClick = useCallback(() => {
    logBiEvent(
      BiData<RequestNowClickBiEvent>({
        evid: 770,
        view: BiContextProvidedValue,
        component: BiContextProvidedValue,
        itemid: deviation.deviationId,
        typeid: deviation.typeId,
        sectionname: sectionname ?? undefined,
        purchase_price: originals?.basePrice,
        completed_orders: completedOrdersCount,
        in_progress_orders: originals?.inProgressOrdersCount,
      })
    );
    dispatchPushAdoptsOrderOriginalDeviationModal(deviation);
  }, [
    logBiEvent,
    dispatchPushAdoptsOrderOriginalDeviationModal,
    deviation,
    sectionname,
    originals,
    completedOrdersCount,
  ]);

  if (
    deviation.originals?.isOpen === false ||
    deviation.extended?.originals?.isOpen === false
  ) {
    return (
      <SolidButton
        className={className}
        variant="main"
        size={size}
        priority="secondary"
        rounded
        disabled
      >
        {t('originals.actions.requestOriginalClosed')}
      </SolidButton>
    );
  }

  return (
    <BrandedButton
      className={className}
      variant="originals"
      size={size}
      onClick={restrictToLoggedIn(
        ReferralType.ORIGINALS_REQUEST,
        handleRequestClick
      )}
    >
      {t('originals.actions.requestOriginal')}
    </BrandedButton>
  );
};
RequestOriginalButton.displayName = 'RequestOriginalButton';

export default RequestOriginalButton;
